import { z } from 'zod';

const localDateTimeArraySchema = z
  .array(z.number().int())
  .min(3)
  .max(9);

export const quotaStatusSchema = z.enum(['active', 'limited', 'blocked']);

export const quotaSchema = z.object({
  total: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  status: quotaStatusSchema,
  refreshAt: z.string().datetime().nullable().optional()
});

export const tokenSchema = z.object({
  token: z.string().min(1),
  tokenType: z.enum(['device', 'user']),
  expiresAt: z.string().datetime(),
  quota: quotaSchema
});

export const urlOrDataUriSchema = z.string().min(1).superRefine((value, ctx) => {
  if (value.startsWith('data:')) {
    return;
  }
  if (
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../')
  ) {
    return;
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return;
    }
  } catch {
    // fall through to issue
  }
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'Invalid url'
  });
});

export const cssColorSchema = z
  .string()
  .min(1)
  .refine(
    (value) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return false;
      }
      if (trimmed.startsWith('#')) {
        return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed);
      }
      if (/^(rgb|hsl)a?\(/i.test(trimmed)) {
        return true;
      }
      if (/^var\(--.+\)$/.test(trimmed)) {
        return true;
      }
      if (/^[a-zA-Z]+$/.test(trimmed)) {
        return true;
      }
      return false;
    },
    { message: 'Invalid CSS color value' }
  );

export const backgroundColorOptionSchema = z.object({
  colorCode: z.string().min(1),
  colorValue: cssColorSchema,
  colorDescription: z.string().optional()
});

export const styleOptionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  previewUrl: urlOrDataUriSchema,
  category: z.string().optional(),
  groupCode: z.string().optional(),
  groupName: z.string().optional(),
  costMultiplier: z.number().positive().optional(),
  updatedAt: z.string().datetime().optional(),
  backgroundColors: z.array(backgroundColorOptionSchema).min(1).optional(),
  defaultBackgroundColorCode: z.string().min(1).optional()
});

export const styleCatalogPageInfoSchema = z.object({
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  totalElements: z.number().int().nonnegative().optional(),
  totalPages: z.number().int().nonnegative().optional(),
  hasNext: z.boolean().optional(),
  hasPrevious: z.boolean().optional()
});

export const styleCatalogResponseSchema = z.object({
  items: z.array(styleOptionSchema),
  pageInfo: styleCatalogPageInfoSchema
});

export const transformStatusSchema = z.enum(['pending', 'processing', 'succeeded', 'failed']);

export const transformAcceptedSchema = z.object({
  transformId: z.string().min(1),
  status: z.enum(['pending', 'processing']),
  estimatedCompletionSeconds: z.number().int().nonnegative().optional()
});

export const transformStatusResponseSchema = z.object({
  transformId: z.string().min(1),
  status: transformStatusSchema,
  resultUrl: z.string().url().optional(),
  errorCode: z.string().optional(),
  completedAt: z.string().datetime().optional(),
  quota: quotaSchema.optional()
});

export const deviceTokenResponseSchema = tokenSchema;
export const authTokenResponseSchema = tokenSchema;

export const styleTransferErrorSchema = z.object({
  code: z.union([z.string(), z.number()]).optional(),
  message: z.string().optional(),
  details: z.unknown().optional()
});

export const quotaResponseSchema = quotaSchema;
export type Quota = z.infer<typeof quotaSchema>;
export type TokenResponse = z.infer<typeof tokenSchema>;
export type BackgroundColorOption = z.infer<typeof backgroundColorOptionSchema>;
export type StyleOption = z.infer<typeof styleOptionSchema>;
export type StyleCatalogPageInfo = z.infer<typeof styleCatalogPageInfoSchema>;
export type StyleCatalogResponse = z.infer<typeof styleCatalogResponseSchema>;
export type TransformAcceptedResponse = z.infer<typeof transformAcceptedSchema>;
export type TransformStatusResponse = z.infer<typeof transformStatusResponseSchema>;
export type QuotaResponse = z.infer<typeof quotaResponseSchema>;

export const apiResponseEnvelopeSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: z.unknown()
});

export const intelliSightStyleProfileSchema = z.object({
  styleCode: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string().optional().nullable(),
  imageExample: z.string().optional().nullable(),
  active: z.boolean().optional(),
  groupCode: z.string().optional().nullable(),
  groupName: z.string().optional().nullable(),
  costMultiplier: z.number().positive().optional().nullable(),
  updatedAt: z.union([z.string(), localDateTimeArraySchema]).optional().nullable(),
  backgroundColors: z
    .array(
      z.object({
        colorCode: z.string().min(1),
        colorValue: cssColorSchema,
        colorDescription: z.string().optional().nullable()
      })
    )
    .optional()
    .nullable(),
  defaultBackgroundColorCode: z.string().optional().nullable()
});

export const intelliSightStyleProfilePageSchema = z.object({
  items: z.array(intelliSightStyleProfileSchema),
  totalElements: z.number().int().nonnegative().optional(),
  totalPages: z.number().int().nonnegative().optional(),
  page: z.number().int().nonnegative().optional(),
  size: z.number().int().nonnegative().optional(),
  hasNext: z.boolean().optional(),
  hasPrevious: z.boolean().optional()
});

export const intelliSightImageStyleResponseSchema = z.object({
  success: z.boolean(),
  styleCode: z.string().optional(),
  styleName: z.string().optional(),
  base64Image: z.string().min(1),
  mimeType: z.string().optional(),
  providerName: z.string().optional().nullable(),
  modelName: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
  backgroundColorCode: z.string().optional().nullable(),
  backgroundColorValue: cssColorSchema.optional().nullable()
});

export const intelliSightNonRegisterResponseSchema = z.object({
  token: z.string().min(1),
  deviceId: z.string().min(1),
  tokenExpiryTime: z.union([z.string().min(1), localDateTimeArraySchema]),
  dailyRequestLimit: z.number().int().nonnegative(),
  remainingRequests: z.number().int().nonnegative(),
  balance: z.number().nullable().optional(),
  accountStatus: z.string().optional(),
  proxyUrl: z.string().optional().nullable()
});

export const intelliSightLoginResponseSchema = z.object({
  token: z.string().min(1),
  username: z.string().nullable().optional(),
  userId: z.number().optional(),
  proxyUrl: z.string().nullable().optional(),
  balance: z.number().int().nonnegative().optional()
});

export const intelliSightRegisterResponseSchema = z.object({
  user: z
    .object({
      userId: z.number().optional(),
      email: z.string().optional(),
      username: z.string().optional(),
      userLevel: z.string().optional()
    })
    .optional(),
  token: z.string().nullable().optional(),
  balance: z.number().int().nonnegative().nullable().optional()
});

export const intelliSightTokenCheckResponseSchema = z.object({
  token: z.string().min(1),
  expiryTime: z.union([z.string().min(1), localDateTimeArraySchema]).nullable().optional(),
  remainingRequests: z.number().int().nonnegative().nullable().optional(),
  clientType: z.string().optional(),
  isActive: z.boolean().optional(),
  proxyUrl: z.string().nullable().optional()
});

export const intelliSightVoucherRedeemResponseSchema = z.object({
  voucherCode: z.string().min(1),
  alias: z.string().min(1),
  email: z.string().email(),
  userId: z.number().int().nonnegative().nullable().optional(),
  redeemedAt: z.union([z.string(), localDateTimeArraySchema]).nullable().optional(),
  token: z.string().min(1),
  balance: z.number().int().nonnegative().nullable().optional()
});

export const intelliSightGoogleAuthResponseSchema = z.object({
  token: z.string().min(1),
  email: z.string().email(),
  username: z.string().nullable().optional(),
  userId: z.number().int().nonnegative().nullable().optional(),
  balance: z.number().int().nonnegative().nullable().optional(),
  tokenExpiryTime: z.union([z.string().min(1), localDateTimeArraySchema]).nullable().optional(),
  isNewUser: z.boolean().optional()
});

